'use client'

import React, { useState } from 'react'
import { formatFontFamily } from '@/lib/themeHelpers'
import { useScrollAnimation, scrollAnimationClasses } from '@/lib/useScrollAnimation'

interface ContactFAQSectionProps {
  surface: "dark" | "light";
  theme: any;
  radius: number;
  fontFamily: string;
  iconWeight: string;
  gradStart: string;
  gradEnd: string;
  angle: number;
  content: any;
}

export default function ContactFAQSection({
  surface,
  theme,
  radius,
  fontFamily,
  gradStart,
  gradEnd,
  angle,
  content
}: ContactFAQSectionProps) {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  const [showCampaignDetails, setShowCampaignDetails] = useState(false);
  const { ref: formRef, isVisible: formVisible } = useScrollAnimation();
  const { ref: faqRef, isVisible: faqVisible } = useScrollAnimation({ rootMargin: '0px 0px -50px 0px' });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    budgetRange: '',
    campaignGoals: '',
    targetLaunchDate: '',
    message: '',
    publication_id: 'https://www.wrll1450.com/'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const backgroundStyle = {
    backgroundImage: surface === "dark" 
      ? `linear-gradient(${angle}deg, ${gradStart}, ${gradEnd})`
      : `linear-gradient(${angle}deg, #f8f9fa, #e9ecef)`,
    fontFamily: formatFontFamily(fontFamily)
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await fetch('https://d1lo84safctgv8.cloudfront.net/storefront-lead', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ 
          name: '', 
          email: '', 
          company: '', 
          phone: '', 
          budgetRange: '', 
          campaignGoals: '', 
          targetLaunchDate: '', 
          message: '',
          publication_id: 'https://www.wrll1450.com/'
        });
        setShowCampaignDetails(false);
      } else {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const hasOptionalData = formData.company || formData.phone || formData.budgetRange || formData.campaignGoals || formData.targetLaunchDate;

  return (
    <section id="contactfaq" className="relative w-full" style={backgroundStyle}>
      <div className="mx-auto max-w-6xl px-6 py-12 lg:py-16 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          
          {/* Contact Form */}
          <div 
            ref={formRef as React.RefObject<HTMLDivElement>}
            className={`${scrollAnimationClasses.transition} ${formVisible ? scrollAnimationClasses.visible : scrollAnimationClasses.hidden}`}
          >
            <div className="mb-6">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-3 leading-tight font-primary" style={{ color: theme.text }}>
                {content.contactTitle}
              </h2>
              <p className="text-lg font-secondary" style={{ color: theme.mutedText }}>
                {content.contactDescription}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Hidden publication_id field */}
              <input
                type="hidden"
                name="publication_id"
                value={formData.publication_id}
              />
              
              {/* Essential Fields - Always Visible */}
              <div className="space-y-4">
                {/* Name and Email Row */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1 font-secondary transition-colors duration-200" style={{ color: theme.text }}>
                      Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2.5 text-sm transition-all duration-300 focus:outline-none font-secondary input-glow hover:border-opacity-80 hover:shadow-sm"
                      style={{
                        backgroundColor: theme.card,
                        border: `1px solid ${theme.border}`,
                        borderRadius: radius,
                        color: theme.text,
                        ['--glow-color' as string]: theme.accent
                      }}
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1 font-secondary transition-colors duration-200" style={{ color: theme.text }}>
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2.5 text-sm transition-all duration-300 focus:outline-none font-secondary input-glow hover:border-opacity-80 hover:shadow-sm"
                      style={{
                        backgroundColor: theme.card,
                        border: `1px solid ${theme.border}`,
                        borderRadius: radius,
                        color: theme.text,
                        ['--glow-color' as string]: theme.accent
                      }}
                      placeholder="you@company.com"
                    />
                  </div>
                </div>

                {/* Message */}
                <div>
                  <label className="block text-sm font-medium mb-1 font-secondary transition-colors duration-200" style={{ color: theme.text }}>
                    Message *
                  </label>
                  <textarea
                    name="message"
                    required
                    rows={3}
                    value={formData.message}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2.5 text-sm transition-all duration-300 focus:outline-none resize-vertical font-secondary input-glow hover:border-opacity-80 hover:shadow-sm"
                    style={{
                      backgroundColor: theme.card,
                      border: `1px solid ${theme.border}`,
                      borderRadius: radius,
                      color: theme.text,
                      ['--glow-color' as string]: theme.accent
                    }}
                    placeholder="Tell us about your advertising goals..."
                  />
                </div>
              </div>

              {/* Campaign Details Toggle */}
              <button
                type="button"
                onClick={() => setShowCampaignDetails(!showCampaignDetails)}
                className="flex items-center gap-2 text-sm font-medium transition-all duration-200 hover:opacity-80 hover:scale-[1.01] hover:translate-x-0.5"
                style={{ color: '#1e3a5f' }}
              >
                <svg
                  className={`w-4 h-4 transition-transform duration-300 ${showCampaignDetails ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
                {showCampaignDetails ? 'Hide marketing goals' : 'Share your marketing goals'}
                {hasOptionalData && !showCampaignDetails && (
                  <span className="ml-1 w-2 h-2 rounded-full" style={{ backgroundColor: theme.accent }} />
                )}
              </button>

              {/* Expandable Campaign Details Section */}
              <div
                className={`overflow-hidden transition-all duration-300 ease-in-out ${
                  showCampaignDetails ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <div className="space-y-4 pt-2">
                  {/* Company and Phone Row */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1 font-secondary transition-colors duration-200" style={{ color: theme.text }}>
                        Company
                      </label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2.5 text-sm transition-all duration-300 focus:outline-none font-secondary input-glow hover:border-opacity-80 hover:shadow-sm"
                        style={{
                          backgroundColor: theme.card,
                          border: `1px solid ${theme.border}`,
                          borderRadius: radius,
                          color: theme.text,
                          ['--glow-color' as string]: theme.accent
                        }}
                        placeholder="Company name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1 font-secondary transition-colors duration-200" style={{ color: theme.text }}>
                        Phone
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2.5 text-sm transition-all duration-300 focus:outline-none font-secondary input-glow hover:border-opacity-80 hover:shadow-sm"
                        style={{
                          backgroundColor: theme.card,
                          border: `1px solid ${theme.border}`,
                          borderRadius: radius,
                          color: theme.text,
                          ['--glow-color' as string]: theme.accent
                        }}
                        placeholder="(555) 000-0000"
                      />
                    </div>
                  </div>

                  {/* Budget Range and Campaign Goals Row */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1 font-secondary transition-colors duration-200" style={{ color: theme.text }}>
                        Budget range
                      </label>
                      <select
                        name="budgetRange"
                        value={formData.budgetRange}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2.5 text-sm transition-all duration-300 focus:outline-none font-secondary input-glow hover:border-opacity-80 hover:shadow-sm cursor-pointer"
                        style={{
                          backgroundColor: theme.card,
                          border: `1px solid ${theme.border}`,
                          borderRadius: radius,
                          color: theme.text,
                          ['--glow-color' as string]: theme.accent
                        }}
                      >
                        <option value="">Select budget</option>
                        <option value="under-5k">Under $5K</option>
                        <option value="5k-15k">$5K - $15K</option>
                        <option value="15k-50k">$15K - $50K</option>
                        <option value="50k-plus">$50K+</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1 font-secondary transition-colors duration-200" style={{ color: theme.text }}>
                        Campaign goals
                      </label>
                      <select
                        name="campaignGoals"
                        value={formData.campaignGoals}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2.5 text-sm transition-all duration-300 focus:outline-none font-secondary input-glow hover:border-opacity-80 hover:shadow-sm cursor-pointer"
                        style={{
                          backgroundColor: theme.card,
                          border: `1px solid ${theme.border}`,
                          borderRadius: radius,
                          color: theme.text,
                          ['--glow-color' as string]: theme.accent
                        }}
                      >
                        <option value="">Select goal</option>
                        <option value="brand-awareness">Brand awareness</option>
                        <option value="lead-generation">Lead generation</option>
                        <option value="event-promotion">Event promotion</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>

                  {/* Target Launch Date */}
                  <div>
                    <label className="block text-sm font-medium mb-1 font-secondary transition-colors duration-200" style={{ color: theme.text }}>
                      Target launch date
                    </label>
                    <input
                      type="date"
                      name="targetLaunchDate"
                      value={formData.targetLaunchDate}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2.5 text-sm transition-all duration-300 focus:outline-none font-secondary input-glow hover:border-opacity-80 hover:shadow-sm cursor-pointer"
                      style={{
                        backgroundColor: theme.card,
                        border: `1px solid ${theme.border}`,
                        borderRadius: radius,
                        color: theme.text,
                        ['--glow-color' as string]: theme.accent
                      }}
                    />
                  </div>
                </div>
              </div>
              
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-2.5 text-sm font-semibold shadow-lg transition-all duration-200 hover:shadow-xl hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 font-primary"
                style={{
                  backgroundColor: theme.button,
                  color: theme.onButton,
                  borderRadius: radius
                }}
              >
                {isSubmitting ? 'Sending...' : 'Send message'}
              </button>

              {/* Status Messages */}
              {submitStatus === 'success' && (
                <div className="mt-3 p-3 rounded-lg animate-fade-in" style={{
                  backgroundColor: surface === 'dark' ? 'rgba(34, 197, 94, 0.1)' : 'rgba(34, 197, 94, 0.1)',
                  border: '1px solid rgba(34, 197, 94, 0.3)',
                  borderRadius: radius
                }}>
                  <p className="text-sm font-medium font-secondary" style={{ color: '#22c55e' }}>
                    ✓ Thank you! We'll be in touch soon.
                  </p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mt-3 p-3 rounded-lg animate-fade-in" style={{
                  backgroundColor: surface === 'dark' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  borderRadius: radius
                }}>
                  <p className="text-sm font-medium font-secondary" style={{ color: '#ef4444' }}>
                    ✗ Something went wrong. Please try again.
                  </p>
                </div>
              )}
            </form>
          </div>

          {/* FAQ Section */}
          <div 
            ref={faqRef as React.RefObject<HTMLDivElement>}
            className={`${scrollAnimationClasses.transition} ${faqVisible ? scrollAnimationClasses.visible : scrollAnimationClasses.hidden}`}
            style={{ transitionDelay: '200ms' }}
          >
            <div className="mb-6">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-3 leading-tight font-primary" style={{ color: theme.text }}>
                {content.faqTitle}
              </h2>
            </div>

            <div className="space-y-3">
              {content.faqItems.map((faq: any, index: number) => (
                <div
                  key={index}
                  className="border-b pb-3 transition-all duration-300 hover:border-opacity-80 group/faq"
                  style={{ 
                    borderColor: theme.border,
                  }}
                >
                  <button
                    onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
                    className="w-full text-left flex justify-between items-start gap-3 py-1.5 px-2 -mx-2 rounded-lg transition-all duration-200 hover:bg-black/5 dark:hover:bg-white/5"
                    style={{ borderRadius: radius }}
                  >
                    <span 
                      className="font-medium text-base font-primary transition-colors duration-200 group-hover/faq:translate-x-0.5" 
                      style={{ color: theme.text }}
                    >
                      {faq.question}
                    </span>
                    <svg
                      className={`w-4 h-4 flex-shrink-0 transition-all duration-200 ${openFAQ === index ? 'rotate-180' : ''} group-hover/faq:scale-110`}
                      fill="none"
                      stroke="currentColor"
                      style={{ color: theme.accent }}
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {openFAQ === index && (
                    <div className="mt-2 animate-fade-in">
                      <p className="text-sm font-secondary" style={{ color: theme.mutedText }}>
                        {faq.answer}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
